import { useReviewDraft } from '@/hooks/useReviewDraft';
import React, { useRef, useState } from 'react';
import { proposalPlan } from '@/utils/offlineRunPlan';
import { AlertTriangle, CheckCircle2, CloudOff, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import RunSendHoldNotice from './RunSendHoldNotice';

const SECTION_LABELS = { name: 'Name', driver: 'Driver', stop_plan: 'Stop plan' };
const STATUS_LABELS = {
  safe: 'Safe to synchronize',
  already_satisfied: 'Already matches online',
  conflict: 'Conflict with online state',
  unavailable: 'Current online evidence unavailable',
  dependency_blocked: 'Waiting for another run change',
};

const DECISION_LABELS = {
  approve: 'Approve for synchronization',
  override: 'Override newer online change',
  keep_online: 'Discard offline change',
};

const driverLabel = (id, item, side) => {
  if (!id) return 'Unassigned';
  if (side === 'current' && item.current?.driver?.name) return item.current.driver.name;
  return item.driver_labels?.[id] || id;
};

const stopLabel = (id, item, side) => {
  if (side === 'current') {
    const stop = item.current?.populated_stops?.find(candidate => candidate.order_id === id);
    const order = stop?.order;
    if (order?.internal_order_number) return `#${order.internal_order_number}`;
    if (order?.recipient_name) return order.recipient_name;
  }
  return item.order_labels?.[id] || id;
};

const valueFor = (section, value, item, side) => {
  if (value?.missing) return 'Run no longer exists online';
  if (value?.evidence_unavailable) return 'Could not refresh';
  if (section === 'name') return value?.name || 'Unnamed run';
  if (section === 'driver') return driverLabel(value?.user_id, item, side);
  const stops = value?.stops || [];
  return stops.length
    ? stops.map(stop => stopLabel(stop.order_id, item, side)).join(' → ')
    : 'No stops';
};

export default function CachedRunProposalCard({
  item, resolvingId, onDecision, onDiscard, onSynchronize, onRename,
  onAssignDriver, onUnassignDriver, onRemoveStop, onSaveAsNew,
  driverUsers = [], isAdmin = false, held = false,
}) {
  const proposal = item.proposal || {};
  const [editingName, setEditingName, captureEditor] = useReviewDraft(false);
  const [proposedName, setProposedName, captureName] = useReviewDraft(proposal.desired?.name || '');
  const nameOpening = useRef(null);
  const planIdentity = JSON.stringify(proposalPlan(proposal));
  if (nameOpening.current && nameOpening.current.identity !== planIdentity) nameOpening.current.retired = true;
  const nameStale = editingName && (!nameOpening.current || nameOpening.current.retired);
  const [editingSection, setEditingSection] = useState(null);
  const [confirmOverrideSection, setConfirmOverrideSection, captureConfirmation] = useReviewDraft(null);
  const changed = proposal.changed_sections || [];
  const current = item.current || { evidence_unavailable: true };
  const busy = resolvingId === proposal.cloud_run_id
    || String(resolvingId || '').startsWith(`${proposal.cloud_run_id}:`);
  const decisions = Object.entries(proposal.review_decisions || {});
  const canSynchronize = decisions.some(([section, record]) => (
    ['approve', 'override', 'keep_online'].includes(record?.decision)
    && !(section === 'stop_plan' && (proposal.dependencies || []).length > 0)
  ));
  const hasConflict = Object.values(item.section_statuses || {}).includes('conflict');

  return (
    <fieldset disabled={held} className="min-w-0"><Card data-testid={`cached-run-proposal-${proposal.cloud_run_id}`}>
      <CardHeader className="flex-row items-start justify-between gap-3">
        <div>
          <RunSendHoldNotice held={held} unqualified={proposal.run_send_eligible === false} />
          <CardTitle className="text-lg">Cached run: {proposal.baseline?.name || proposal.cloud_run_id}</CardTitle>
          <p className="mt-1 text-sm text-stone-600">
            Compare what this computer started with, what was proposed offline, and what exists online now.
          </p>
        </div>
        {isAdmin && (
          <Button variant="outline" disabled={busy} onClick={() => onDiscard(item)}>
            Discard all offline changes
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {current.evidence_unavailable && (
          <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            <CloudOff className="h-4 w-4 shrink-0" /> Current cloud evidence could not be refreshed. Decisions are disabled.
          </div>
        )}
        {changed.map((section) => {
          const status = item.section_statuses?.[section] || 'unavailable';
          const saved = proposal.review_decisions?.[section]?.decision;
          const sectionBusy = resolvingId === `${proposal.cloud_run_id}:${section}`;
          const canApprove = status === 'safe' || status === 'already_satisfied';
          const dependencyBlocked = status === 'dependency_blocked';
          const canOverride = status === 'conflict'
            && current.status === 'draft'
            && !current.missing
            && !current.evidence_unavailable;
          return (
            <section key={section} className={`rounded-xl border border-stone-200 p-4 ${dependencyBlocked ? 'bg-stone-50' : ''}`} data-testid={`cached-run-section-${section}`}>
              <div className="mb-3 flex items-center justify-between gap-2">
                <h3 className="font-semibold text-stone-900">{SECTION_LABELS[section]}</h3>
                <span className={`flex items-center gap-1 text-xs font-medium ${status === 'conflict' ? 'text-red-700' : dependencyBlocked ? 'text-amber-700' : 'text-emerald-700'}`}>
                  {status === 'conflict' || dependencyBlocked ? <AlertTriangle className="h-3.5 w-3.5" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                  {STATUS_LABELS[status]}
                </span>
              </div>
              <div className="grid gap-2 md:grid-cols-3">
                {[
                  ['Starting point', proposal.baseline, 'baseline'],
                  ['Offline proposal', proposal.desired, 'desired'],
                  ['Online now', current, 'current'],
                ].map(([label, value, side]) => (
                  <div key={label} className="rounded-lg bg-stone-50 p-3">
                    <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-stone-500">{label}</div>
                    <div className="break-words text-sm text-stone-900">{valueFor(section, value, item, side)}</div>
                  </div>
                ))}
              </div>
              {canOverride && (
                <div className="mt-3 text-xs font-semibold text-stone-700">
                  Newer online activity requires explicit override
                </div>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {isAdmin && (
                  <>
                    {dependencyBlocked ? (
                      <div className="w-full rounded-lg border border-stone-200 bg-stone-100 p-3 text-sm text-stone-700">
                        <div className="font-semibold">This stop-plan change must wait.</div>
                        {proposal.dependency_cycle ? (
                          <div className="mt-1">These run changes depend on each other. Edit one stop plan to break the cycle.</div>
                        ) : (proposal.dependencies || []).map(dependency => (
                          <div key={dependency.cloud_run_id} className="mt-1">
                            First synchronize the required order removal from {dependency.run_name}.
                          </div>
                        ))}
                      </div>
                    ) : (
                      <>
                    {canApprove ? (
                      <Button size="sm" disabled={busy} onClick={() => onDecision(item, section, 'approve')}>
                        {sectionBusy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
                        Approve for synchronization
                      </Button>
                    ) : (
                      !canOverride && <span className="rounded-md bg-stone-100 px-3 py-2 text-xs font-semibold text-stone-700">
                        {status === 'unavailable'
                          ? 'Current online evidence required'
                          : 'Cannot overwrite newer online activity'}
                      </span>
                    )}
                    {canOverride && (
                      <Button
                        size="sm"
                        variant="destructive"
                        disabled={busy}
                        onClick={() => setConfirmOverrideSection({ section, item })}
                      >
                        Override newer online change
                      </Button>
                    )}
                    <Button size="sm" variant="outline" disabled={busy || current.evidence_unavailable} onClick={() => onDecision(item, section, 'keep_online')}>
                      Discard offline change
                    </Button>
                      </>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busy}
                      onClick={() => {
                        setEditingName(false);
                        setEditingSection(value => value === section ? null : section);
                      }}
                    >
                      Edit offline change
                    </Button>
                  </>
                )}
                {saved && DECISION_LABELS[saved] && (
                  <span className="text-xs text-blue-700">
                    Decision: {DECISION_LABELS[saved] || saved}
                    {dependencyBlocked ? ' — waiting for prerequisite' : ''}
                  </span>
                )}
              </div>
              {isAdmin && confirmOverrideSection?.section === section && confirmOverrideSection.item === item && (
                <div className="mt-3 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-950">
                  <div className="font-semibold">The online value is newer.</div>
                  <p className="mt-1">
                    This deliberate administrator action will replace it with the offline proposal. Current safety rules will still be checked.
                  </p>
                  <div className="mt-3 flex gap-2">
                    <Button
                      size="sm"
                      variant="destructive"
                      disabled={busy}
                      onClick={async () => {
                        const current = captureConfirmation();
                        if (await onDecision(item, section, 'override') && current()) setConfirmOverrideSection(null);
                      }}
                    >
                      Yes, override online change
                    </Button>
                    <Button size="sm" variant="outline" disabled={busy} onClick={() => setConfirmOverrideSection(null)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
              {isAdmin && editingSection === section && (
                <div className="mt-3 rounded-lg border border-blue-100 bg-blue-50 p-3">
                  <div className="mb-2 text-xs font-semibold text-blue-900">Edit offline proposal</div>
                  {section === 'name' && !editingName && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy}
                      onClick={() => {
                        setProposedName(proposal.desired?.name || '');
                        nameOpening.current = { identity: planIdentity, submit: onRename };
                        setEditingName(true);
                      }}
                    >
                      Change proposed name
                    </Button>
                  )}
                  {section === 'name' && editingName && (
                    <form
                      className="flex flex-wrap items-center gap-2"
                      onSubmit={async (event) => {
                        event.preventDefault();
                        if (nameStale || !nameOpening.current) return;
                        const nextName = proposedName.trim();
                        if (!nextName || nextName === (proposal.desired?.name || '')) return;
                        const currentEditor = captureEditor(); const currentName = captureName();
                        if (await nameOpening.current.submit(item, nextName) && currentEditor() && currentName()) setEditingName(false);
                      }}
                    >
                      <Input
                        autoFocus
                        className="h-9 w-64 bg-white"
                        value={proposedName}
                        onChange={event => setProposedName(event.target.value)}
                        aria-label="Proposed run name"
                      />
                      <Button size="sm" type="submit" disabled={busy || nameStale || !proposedName.trim()}>Save proposed name</Button>
                      <Button size="sm" type="button" variant="ghost" disabled={busy} onClick={() => setEditingName(false)}>Cancel</Button>
                      {nameStale && <p className="w-full text-sm text-amber-900">This plan changed. Your text is kept here. Review the current plan, then cancel and reopen this editor before applying a change.</p>}
                    </form>
                  )}
                  {section === 'driver' && (
                    <div className="flex flex-wrap gap-2">
                      <select
                        className="h-9 rounded-md border border-stone-300 bg-white px-2 text-sm"
                        value={proposal.desired?.user_id || ''}
                        disabled={busy}
                        onChange={event => (event.target.value ? onAssignDriver(item, event.target.value) : onUnassignDriver(item))}
                      >
                        <option value="">Unassigned</option>
                        {driverUsers.map(driver => <option key={driver.id} value={driver.id}>{driver.name || driver.username || driver.id}</option>)}
                      </select>
                    </div>
                  )}
                  {section === 'stop_plan' && (
                    <div className="space-y-2">
                      {(proposal.desired?.stops || []).map(stop => (
                        <div key={stop.order_id} className="flex items-center justify-between gap-2 rounded bg-white px-2 py-1 text-sm">
                          <span>{stopLabel(stop.order_id, item, 'desired')}</span>
                          <Button size="sm" variant="ghost" disabled={busy} onClick={() => onRemoveStop(item, stop.order_id)}>Remove</Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </section>
          );
        })}
        {isAdmin && (
          <div className="flex items-center justify-between gap-3 border-t border-stone-200 pt-4">
            <p className="text-xs text-stone-500">Only approved or discarded sections are submitted. Unresolved sections remain here.</p>
            <Button disabled={busy || !canSynchronize || current.evidence_unavailable || proposal.run_send_eligible === false} onClick={() => onSynchronize(item)}>
              {resolvingId === `${proposal.cloud_run_id}:synchronize` ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
              Synchronize reviewed sections
            </Button>
          </div>
        )}
        {isAdmin && hasConflict && (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
            <p className="text-xs text-amber-900">If the original run cannot safely accept this plan, try creating a separate draft. Current cloud eligibility is checked before anything is created.</p>
            <Button variant="outline" disabled={busy || current.evidence_unavailable || proposal.run_send_eligible === false} onClick={() => onSaveAsNew(item)}>
              {resolvingId === `${proposal.cloud_run_id}:save-as-new` ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
              Save offline plan as new draft
            </Button>
          </div>
        )}
        {!isAdmin && <p className="text-xs text-stone-500">An administrator must decide these changes.</p>}
      </CardContent>
    </Card></fieldset>
  );
}
